import React, {useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo} from 'react';
import {createPortal} from 'react-dom';
import {RangeKeyDict, RangeFocus} from 'react-date-range';
import {subMonths, addMonths} from 'date-fns';
import {useSelector} from 'react-redux';
import * as reduxSelectors from 'components/store/application.reducers';
import {CalendarIcon} from 'lucide-react';
import {TextInput} from 'components/common/TextInput.component';
import 'components/common/calendar/theme.scss';
import {YearGridState, MonthGridState} from 'components/common/calendar/CalendarNavigator.component';
import {YearGridPanel} from 'components/common/calendar/YearGridPanel.component';
import {MonthGridPanel} from 'components/common/calendar/MonthGridPanel.component';
import {CalendarPickerPanel} from 'components/common/calendar/CalendarPickerPanel.component';
import {CalendarFooter} from 'components/common/calendar/CalendarFooter.component';
import {
    CalendarQuickSelect,
    DateRange,
    QuickOption,
    matchQuickOption
} from 'components/common/calendar/CalendarQuickSelect.component';
import {
    getDateFnsLocale,
    buildRange,
    formatDisplayValue,
    sameOrAfter,
    usePopoverClose,
} from 'components/common/calendar/calendarHelpers';

interface NewCalendarProps {
    value?: string;
    placeholder?: string;
    onChange?: (range: DateRange) => void;
    initialStartDate?: Date;
    initialEndDate?: Date;
    translate?: (key: string) => string;
    disabled?: boolean;
    disablePastDates?: boolean;
    /** Earliest selectable date. Takes precedence over disablePastDates when set. */
    minDate?: Date;
    id?: string;
    showQuickSelect?: boolean;
    /** When true only one calendar month is shown (single-panel mode). Default: false */
    singlePanel?: boolean;
    /** Extra class names applied to the calendar input. */
    fieldClassName?: string;
}

export const NewCalendar: React.FC<NewCalendarProps> = ({
                                                            value,
                                                            placeholder,
                                                            onChange,
                                                            initialStartDate = new Date(),
                                                            initialEndDate = new Date(),
                                                            translate,
                                                            disabled = false,
                                                            disablePastDates = false,
                                                            minDate: minDateProp,
                                                            id,
                                                            showQuickSelect = false,
                                                            singlePanel = false,
                                                            fieldClassName,
                                                        }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [appliedRange, setAppliedRange] = useState(() => buildRange(initialStartDate, initialEndDate));
    const [pendingRange, setPendingRange] = useState(() => buildRange(initialStartDate, initialEndDate));
    const [focusedRange, setFocusedRange] = useState<RangeFocus>([0, 0]);
    const [sharedPreview, setSharedPreview] = useState<{ startDate: Date; endDate: Date; color: string } | null>(null);
    const [showLeftDate, setShowLeftDate] = useState<Date>(() => new Date());
    const [showRightDate, setShowRightDate] = useState<Date>(() => addMonths(new Date(), 1));
    const [calendarLeftKey, setCalendarLeftKey] = useState(0);
    const [calendarRightKey, setCalendarRightKey] = useState(0);
    const [yearGrid, setYearGrid] = useState<YearGridState | null>(null);
    const [monthGrid, setMonthGrid] = useState<MonthGridState | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
    const yearGridRef = useRef<HTMLDivElement>(null);
    const monthGridRef = useRef<HTMLDivElement>(null);
    // Refs so makeShownDateChangeHandler (empty-dep useCallback) can read current values
    const showLeftDateRef = useRef(showLeftDate);
    const showRightDateRef = useRef(showRightDate);
    showLeftDateRef.current = showLeftDate;
    showRightDateRef.current = showRightDate;

    usePopoverClose(!!yearGrid, yearGridRef, () => setYearGrid(null));
    usePopoverClose(!!monthGrid, monthGridRef, () => setMonthGrid(null));

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (yearGridRef.current?.contains(target)) return;
            if (monthGridRef.current?.contains(target)) return;
            // Popup is portaled to <body>, so it is outside containerRef — treat
            // clicks inside it as inside.
            if (popupRef.current?.contains(target)) return;
            if (containerRef.current && !containerRef.current.contains(target)) cancelDates();
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (yearGrid) setYearGrid(null);
                else if (monthGrid) setMonthGrid(null);
                else cancelDates();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, yearGrid, monthGrid, appliedRange]);

    // Position the portaled popup relative to the trigger. Runs before paint so
    // the popup never flashes at the wrong spot. Keeps it inside the viewport and
    // flips above the trigger when there isn't enough room below.
    useLayoutEffect(() => {
        if (!isOpen) {
            setPopupPos(null);
            return;
        }
        const updatePosition = () => {
            const trigger = containerRef.current;
            const popup = popupRef.current;
            if (!trigger || !popup) return;
            const rect = trigger.getBoundingClientRect();
            const popupW = popup.offsetWidth;
            const popupH = popup.offsetHeight;
            const margin = 4;

            let top = rect.bottom + margin;
            if (top + popupH > window.innerHeight && rect.top - margin - popupH >= 0) {
                top = rect.top - margin - popupH;
            }
            let left = rect.left;
            if (left + popupW > window.innerWidth - margin) {
                left = Math.max(margin, window.innerWidth - margin - popupW);
            }
            setPopupPos({top, left});
        };
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen]);

    const openModal = () => {
        if (disabled) return;

        // Toggle behavior: clicking the trigger while open should close the popup.
        if (isOpen) {
            cancelDates();
            return;
        }

        setPendingRange(appliedRange);
        setFocusedRange([0, 0]);
        setSharedPreview(null);

        const startMonth = new Date(appliedRange.startDate.getFullYear(), appliedRange.startDate.getMonth(), 1);
        setShowLeftDate(startMonth);
        setCalendarLeftKey(k => k + 1);

        if (!singlePanel) {
            const endMonth = new Date(appliedRange.endDate.getFullYear(), appliedRange.endDate.getMonth(), 1);
            const panel2 = endMonth > startMonth ? endMonth : addMonths(startMonth, 1);
            setShowRightDate(panel2);
            setCalendarRightKey(k => k + 1);
        }

        setIsOpen(true);
    };

    const handleChange = (rangesByKey: RangeKeyDict) => {
        const {startDate, endDate} = rangesByKey.date;
        if (singlePanel) {
            // Single-date mode: the first (and only) click picks one date.
            // focusedRange starts at [0,0] so startDate is always the clicked date.
            const selected = startDate!;
            const newRange = buildRange(selected, selected);
            setAppliedRange(newRange);
            setPendingRange(newRange);
            onChange?.({startDate: selected, endDate: selected});
            setIsOpen(false);
            return;
        }
        setPendingRange(prev => ({...prev, startDate, endDate}));
    };

    const applyDates = () => {
        setAppliedRange(pendingRange);
        onChange?.({startDate: pendingRange.startDate, endDate: pendingRange.endDate});
        setIsOpen(false);
    };

    const cancelDates = () => {
        setPendingRange(appliedRange);
        setIsOpen(false);
    };

    const applyQuickOption = (opt: QuickOption) => {
        const range = opt.getRange();
        setPendingRange(buildRange(range.startDate, range.endDate));
    };

    const handleYearSelect = (year: number) => {
        if (!yearGrid) return;
        const newDate = new Date(year, yearGrid.month, 1);
        if (yearGrid.monthIdx === 0) {
            setShowLeftDate(newDate);
            setCalendarLeftKey(k => k + 1);
            if (sameOrAfter(newDate, showRightDate)) {
                setShowRightDate(addMonths(newDate, 1));
                setCalendarRightKey(k => k + 1);
            }
        } else {
            setShowRightDate(newDate);
            setCalendarRightKey(k => k + 1);
            if (sameOrAfter(showLeftDate, newDate)) {
                setShowLeftDate(subMonths(newDate, 1));
                setCalendarLeftKey(k => k + 1);
            }
        }
        setYearGrid(null);
    };

    const handleMonthSelect = (month: number, year: number) => {
        if (!monthGrid) return;
        const newDate = new Date(year, month, 1);
        if (monthGrid.panelIdx === 0) {
            setShowLeftDate(newDate);
            setCalendarLeftKey(k => k + 1);
            if (sameOrAfter(newDate, showRightDate)) {
                setShowRightDate(addMonths(newDate, 1));
                setCalendarRightKey(k => k + 1);
            }
        } else {
            setShowRightDate(newDate);
            setCalendarRightKey(k => k + 1);
            if (sameOrAfter(showLeftDate, newDate)) {
                setShowLeftDate(subMonths(newDate, 1));
                setCalendarLeftKey(k => k + 1);
            }
        }
        setMonthGrid(null);
    };

    const makeShownDateChangeHandler = useCallback((panelIdx: 0 | 1) => (date: Date) => {
        if (panelIdx === 0) {
            setShowLeftDate(date);
            if (!singlePanel && sameOrAfter(date, showRightDateRef.current)) {
                setShowRightDate(addMonths(date, 1));
                setCalendarRightKey(k => k + 1);
            }
        } else {
            setShowRightDate(date);
            if (sameOrAfter(showLeftDateRef.current, date)) {
                setShowLeftDate(subMonths(date, 1));
                setCalendarLeftKey(k => k + 1);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singlePanel]);


    // Use the active app language (state.translations.language) so the calendar
    // follows Ctrl+Alt+L language switches. Falls back to the user's profile
    // language if the active one isn't set yet (app boot).
    const appLanguage = useSelector(reduxSelectors.getLanguage);
    const userLanguage = useSelector(reduxSelectors.getUsersCurrentLanguage);
    const langCode = appLanguage ?? userLanguage ?? 'en';
    const locale = useMemo(() => getDateFnsLocale(langCode), [langCode]);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = minDateProp ?? (disablePastDates ? today : undefined);
    const displayValue = value ?? formatDisplayValue(appliedRange.startDate, appliedRange.endDate, langCode);
    const t = (key: string, fallback: string) => translate?.(key) ?? fallback;
    const activeQuickIdx = matchQuickOption(pendingRange.startDate, pendingRange.endDate);

    const stableRangeObj = useRef({startDate: pendingRange.startDate, endDate: pendingRange.endDate, key: 'date'});
    const stableRanges = useRef([stableRangeObj.current]);
    stableRangeObj.current.startDate = pendingRange.startDate;
    stableRangeObj.current.endDate = pendingRange.endDate;

    const handlePreviewChange = useCallback((value: Date | null) => {
        if (!value) {
            setSharedPreview(null);
            return;
        }
        if (focusedRange[1] === 1) {
            let start = stableRangeObj.current.startDate;
            let end = value;
            if (end < start) [start, end] = [end, start];
            setSharedPreview({startDate: start, endDate: end, color: 'var(--ga-color-primary)'});
        } else {
            setSharedPreview({startDate: value, endDate: value, color: 'var(--ga-color-primary)'});
        }
    }, [focusedRange]);


    const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openModal();
        }
    };

    return (
        <div ref={containerRef} className="new-calendar-wrapper">
            <div
                role="button"
                aria-label={t('calendar.calendar', 'Open calendar')}
                aria-expanded={isOpen}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                onClick={openModal}
                onKeyDown={handleTriggerKeyDown}
                style={{cursor: disabled ? 'default' : 'pointer'}}
            >
                <TextInput
                    id={id ?? 'new-calendar-input'}
                    value={displayValue}
                    placeholder={placeholder ?? 'YYYY-MM-DD - YYYY-MM-DD'}
                    readOnly
                    disabled={disabled}
                    tabIndex={-1}
                    rightIcon={<CalendarIcon size={16}/>}
                    {...(fieldClassName && {className: fieldClassName})}
                />
            </div>

            {isOpen && createPortal(
                <div
                    ref={popupRef}
                    className={`calendar-popup${singlePanel ? ' calendar-popup--single' : ''}`}
                    style={{
                        position: 'fixed',
                        top: popupPos?.top ?? 0,
                        left: popupPos?.left ?? 0,
                        visibility: popupPos ? 'visible' : 'hidden',
                    }}
                >
                    <div className="calendar-popup-body">
                        {showQuickSelect && (
                            <CalendarQuickSelect
                                activeQuickIdx={activeQuickIdx}
                                onSelect={applyQuickOption}
                                translate={translate}
                            />
                        )}

                        <CalendarPickerPanel
                            singlePanel={singlePanel}
                            calendarLeftKey={calendarLeftKey}
                            showLeftDate={showLeftDate}
                            onShownDateChange0={makeShownDateChangeHandler(0)}
                            calendarRightKey={calendarRightKey}
                            showRightDate={showRightDate}
                            onShownDateChange1={makeShownDateChangeHandler(1)}
                            ranges={stableRanges.current}
                            onChange={handleChange}
                            locale={locale}
                            focusedRange={focusedRange}
                            onRangeFocusChange={setFocusedRange}
                            preview={sharedPreview ?? undefined}
                            onPreviewChange={handlePreviewChange}
                            {...(minDate && {minDate})}
                            yearGrid={yearGrid}
                            monthGrid={monthGrid}
                            setYearGrid={setYearGrid}
                            setMonthGrid={setMonthGrid}
                        />
                    </div>

                    {!singlePanel &&
                        <CalendarFooter
                            startDate={pendingRange.startDate}
                            endDate={pendingRange.endDate}
                            onApply={applyDates}
                            onCancel={cancelDates}
                            translate={translate}
                            langCode={langCode}
                        />
                    }
                </div>,
                document.body
            )}

            {yearGrid && createPortal(
                <YearGridPanel ref={yearGridRef} selectedYear={yearGrid.year} onSelect={handleYearSelect}
                               style={{top: yearGrid.top, left: yearGrid.left}}/>,
                document.body
            )}
            {monthGrid && createPortal(
                <MonthGridPanel ref={monthGridRef} selectedMonth={monthGrid.month} selectedYear={monthGrid.year}
                                onSelect={handleMonthSelect} style={{top: monthGrid.top, left: monthGrid.left}}
                                locale={locale}/>,
                document.body
            )}
        </div>
    );
};

export default NewCalendar;

